import { Parser, Tokens } from 'marked';
import { MarkedOptions, MarkedRenderer } from 'ngx-markdown';

/**
 * Applies custom formatting to HTML rendering of Markdown
 */
export function markedOptionsFactory(): MarkedOptions {
    const renderer = new MarkedRenderer();
    const parser = new Parser();

    /*
        All we want to do is add a css class to the outer DOM elements. But life isn't that simple. To override
        the renderer, we have to reimplement it. So most of this logic below is copied from the baseline marked.js
        implementation (with some heavy rewriting to clean it up).
    */

    // Add css class to <blockquote>
    renderer.blockquote = ({ tokens }: Tokens.Blockquote): string => {
        const body = parser.parse(tokens);
        return `<blockquote class="blockquote">${body}</blockquote>`;
    };

    // Add css class to <table>
    renderer.table = (token: Tokens.Table): string => {
        let header = parser.renderer.tablerow({
            text: token.header.map(h => parser.renderer.tablecell(h)).join(''),
        });
        if (header) {
            header = `<thead>${header}</thead>`;
        }

        let body = token.rows
            .map(row => {
                const cells = row.map(cell => parser.renderer.tablecell(cell)).join('');
                return parser.renderer.tablerow({ text: cells });
            })
            .join('');
        if (body) {
            body = `<tbody>${body}</tbody>`;
        }

        return `<table class="table table-striped">${header}${body}</table>`;
    };

    // open links in new tab
    renderer.link = ({ href, title, tokens }: Tokens.Link): string => {
        const text = parser.parseInline(tokens);
        return `<a href="${href}" title="${title || ''}" target="_blank" rel="noopener noreferrer">${text}</a>`;
    };

    // Strip HTML comments from raw-HTML tokens. The Copilot stream carries control-marker
    // comments (e.g. `<!-- BEGIN_ANSWERING -->`), and Angular's DomSanitizer removes HTML
    // comments from the rendered output — logging a "sanitizing HTML stripped some content"
    // warning on every re-render while a response streams in. Removing the comment nodes here
    // (using the browser's HTML parser, so only real comment nodes are touched — never text)
    // produces identical rendered HTML with nothing left for the sanitizer to strip, so the
    // warning never fires. An unterminated comment (a marker split across streamed chunks) is
    // parsed as a comment-to-end and removed too. Comments inside fenced code blocks arrive as
    // `code` tokens (already escaped), so they never reach this renderer and render as written.
    renderer.html = ({ text }: Tokens.HTML): string => {
        const template = document.createElement('template');
        template.innerHTML = text;
        const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_COMMENT);
        const comments: Comment[] = [];
        while (walker.nextNode()) {
            comments.push(walker.currentNode as Comment);
        }
        comments.forEach(comment => comment.remove());
        return template.innerHTML;
    };

    return {
        renderer: renderer,
        gfm: true,
        breaks: false,
        pedantic: false,
    };
}
